import {
  ArmchairIcon,
  CalendarIcon,
  CarIcon,
  FuelIcon,
  GaugeIcon,
  MapPinIcon,
} from "lucide-react";
import { ReactNode } from "react";
import {
  ArrowRightIcon,
  StarFilledIcon,
  StopwatchIcon,
} from "@radix-ui/react-icons";
import { formatDate } from "@/lib/Formatters/formatDate";
import { RelocationInclusion } from "@/gql/schema";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { Mark } from "@/components/catalyst/mark";
import { RelocationListItem } from "@/app/Relocations/GraphQL/relocationListQuery";
import { useRelocation } from "@/app/Relocations/Hooks/useRelocation";
import { Badge } from "@/components/catalyst/badge";
import { relocationStatusColorMap } from "@/app/Relocations/Utils/relocationStatusColorMap";
import { useTranslations } from "use-intl";

export function RelocationSummaryCard({
  relocationId,
}: {
  relocationId: string;
}) {
  const relocation = useRelocation(relocationId);
  const t = useTranslations("relocation");

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <MapPinIcon className="h-5 w-5 text-gray-500" />
            <span>{relocation.departureCity.name}</span>
            <ArrowRightIcon className="h-4 w-4 text-gray-400" />
            <span>{relocation.deliveryCity.name}</span>
          </div>
          <Badge color={relocationStatusColorMap[relocation.status]}>
            {relocation.status}
          </Badge>
        </div>
        <div className="text-sm text-gray-500">#{relocation.reference}</div>
      </div>

      <div className="flex gap-6 p-4">
        {/* Vehicle Image */}
        <div className="shrink-0">
          <img
            src={relocation.vehicle.heroImage}
            alt={relocation.vehicle.name}
            className="h-32 w-48 rounded-md object-cover"
          />
        </div>

        {/* Details Grid */}
        <div className="grid flex-1 grid-cols-2 gap-x-8 gap-y-4">
          {/* Vehicle Info */}
          <div>
            <SectionLabel>{t("summaryCard.vehicle")}</SectionLabel>
            <div className="mt-1 flex items-center gap-2">
              <CarIcon className="h-4 w-4 text-gray-400" />
              <span className="font-medium text-gray-900">
                {relocation.vehicle.name}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <ArmchairIcon className="h-3.5 w-3.5" />
                {t("summaryCard.seats", {
                  count: relocation.vehicle.seatbelts,
                })}
              </span>
              <span className="flex items-center gap-1">
                <FuelIcon className="h-3.5 w-3.5" />
                {relocation.vehicle.fuel}
              </span>
            </div>
          </div>

          {/* Route Info */}
          <div>
            <SectionLabel>{t("summaryCard.route")}</SectionLabel>
            <div className="mt-1 space-y-1 text-sm">
              <div className="flex items-start gap-2">
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-gray-900 text-xs font-bold text-white">
                  A
                </div>
                <div>
                  <div className="font-medium text-gray-900">
                    {relocation.departureOffice.name}
                  </div>
                  <div className="text-gray-500">
                    {relocation.departureOffice.address?.city}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-gray-900 text-xs font-bold text-white">
                  B
                </div>
                <div>
                  <div className="font-medium text-gray-900">
                    {relocation.deliveryOffice.name}
                  </div>
                  <div className="text-gray-500">
                    {relocation.deliveryOffice.address?.city}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div>
            <SectionLabel>{t("summaryCard.availability")}</SectionLabel>
            <div className="mt-1 flex items-center gap-2 text-sm">
              <CalendarIcon className="h-4 w-4 text-gray-400" />
              <span className="text-gray-900">
                {formatDate(relocation.available_from_date)} —{" "}
                {formatDate(relocation.available_to_date)}
              </span>
            </div>
          </div>

          {/* Deal Details */}
          <div>
            <SectionLabel>{t("summaryCard.deal")}</SectionLabel>
            <div className="mt-1 flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-1.5">
                <StopwatchIcon className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">
                  {relocation.hire_units_allowed}{" "}
                  {t(`hire_unit_type.${relocation.hire_unit_type}`)}s
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <GaugeIcon className="h-4 w-4 text-gray-400" />
                <span className="text-gray-900">
                  {relocation.distance_allowed
                    ? `${relocation.distance_allowed} ${relocation.measurement}`
                    : t("summaryCard.unlimited")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inclusions */}
      {relocation.inclusions.length > 0 ? (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
          <div className="flex flex-wrap gap-2">
            {relocation.inclusions.map((inclusion) => (
              <InclusionBadge
                key={inclusion.id}
                inclusion={inclusion}
                relocation={relocation}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
      {children}
    </div>
  );
}

export function IconAndText({
  Icon,
  text,
  highlight = false,
  label,
}: {
  Icon: any;
  text: ReactNode;
  highlight?: boolean;
  label?: string;
}) {
  return (
    <li>
      <Mark when={highlight} label={label}>
        <Icon className="mr-2 inline h-4 w-4" />
        <span>{text}</span>
      </Mark>
    </li>
  );
}

function InclusionBadge({
  inclusion,
  relocation,
}: {
  inclusion: Pick<
    RelocationInclusion,
    "value" | "description" | "has_free_tank"
  >;
  relocation: Pick<RelocationListItem, "currency">;
}) {
  const t = useTranslations("relocation");

  if (inclusion.has_free_tank) {
    return (
      <Badge color="green">
        <FuelIcon className="h-3 w-3" />
        {t("summaryCard.freeTank")}
      </Badge>
    );
  }

  const label = inclusion.description?.replace(/<[^>]*>/g, "") ?? "";
  const value = inclusion.value
    ? formatCurrency(inclusion.value, relocation.currency)
    : null;

  return (
    <Badge color="amber">
      <StarFilledIcon className="h-3 w-3" />
      {value ? `${value} ${label}` : label}
    </Badge>
  );
}
